import { createVendor, listVendorsByCompanyId } from '../graphql/companyResourceQueries';

export type VendorCatalogItem = {
    id: string;
    name?: string | null;
    archived?: boolean | null;
};

const createByKey = new Map<string, Promise<VendorCatalogItem>>();

function asVendor(data: unknown): VendorCatalogItem | null {
    if (!data || typeof data !== 'object' || !('id' in data) || typeof (data as { id?: unknown }).id !== 'string') {
        return null;
    }
    return data as VendorCatalogItem;
}

export function findVendorByName(
    vendors: VendorCatalogItem[],
    name: string,
): VendorCatalogItem | undefined {
    const key = name.trim().toLowerCase();
    if (!key) return undefined;
    return vendors.find(
        (vendor) => !vendor.archived && (vendor.name || '').trim().toLowerCase() === key,
    );
}

export function findVendorByIdOrName(
    vendors: VendorCatalogItem[],
    value: string,
): VendorCatalogItem | undefined {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return vendors.find((vendor) => vendor.id === trimmed) || findVendorByName(vendors, trimmed);
}

export async function addVendorFromName(
    companyId: string,
    rawName: string,
    existing: VendorCatalogItem[] = [],
): Promise<VendorCatalogItem> {
    const name = rawName.trim();
    if (!name) {
        throw new Error('Vendor name is required.');
    }

    const localMatch = findVendorByName(existing, name);
    if (localMatch) return localMatch;

    const key = `${companyId}::${name.toLowerCase()}`;
    const inFlight = createByKey.get(key);
    if (inFlight) return inFlight;

    const createPromise = (async () => {
        const latest = (await listVendorsByCompanyId(companyId)) as VendorCatalogItem[];
        const latestMatch = findVendorByName(latest || [], name);
        if (latestMatch) return latestMatch;

        const created = asVendor(await createVendor({ companyId, name }));
        if (!created) {
            throw new Error('Failed to create vendor.');
        }
        return { ...created, name: created.name || name };
    })().finally(() => {
        createByKey.delete(key);
    });

    createByKey.set(key, createPromise);
    return createPromise;
}
